const USER_INFO_KEY = "userInfo";
const USER_PERMISSIONS_KEY = "user-permissions";
const IS_AUTHENTICATED_KEY = "isAuthenticated";
/** Same key as program-scope.ts — keep in sync. */
const PERKPOINT_MANAGER_SESSION_KEY = "perkpoint.managerSession";
const PERKPOINT_SCOPE_EVENT = "perkpoint-scope";

type UserInfoLike = {
  roleCode?: string;
  perkProgramDetails?: {
    programId?: string | number;
    programCode?: string;
  };
};

/**
 * Perk Program Admin: seed the manager session from getCurrentUserInfo
 * perkProgramDetails so they land in program-scoped UI without "Log in as manager".
 * Portfolio admins start in portfolio (clear any leftover session on login).
 */
function syncPerkPointSessionFromUserInfo(data: unknown) {
  if (typeof window === "undefined") return;
  const user = (data ?? {}) as UserInfoLike;
  const role = user.roleCode;

  if (role === "PERK_POINT_PROGRAM") {
    const details = user.perkProgramDetails;
    if (!details?.programId) return;
    const programId = String(details.programId);
    const programCode = details.programCode || "";
    const session = {
      programId,
      programName: programCode || programId,
      programCode,
      startedAt: new Date().toISOString(),
    };
    localStorage.setItem(PERKPOINT_MANAGER_SESSION_KEY, JSON.stringify(session));
    window.dispatchEvent(new Event(PERKPOINT_SCOPE_EVENT));
    return;
  }

  // Fresh login as any other role: drop a leftover program-manager session.
  localStorage.removeItem(PERKPOINT_MANAGER_SESSION_KEY);
  window.dispatchEvent(new Event(PERKPOINT_SCOPE_EVENT));
}

export function setStoredUserInfo(data: unknown) {
  const value = JSON.stringify(data);
  sessionStorage.setItem(USER_INFO_KEY, value);
  localStorage.setItem(USER_INFO_KEY, value);
  syncPerkPointSessionFromUserInfo(data);
}

export function setStoredUserPermissions(data: unknown) {
  const value = JSON.stringify(data);
  sessionStorage.setItem(USER_PERMISSIONS_KEY, value);
  localStorage.setItem(USER_PERMISSIONS_KEY, value);
}

/**
 * Ensures this tab's sessionStorage has auth data for existing readers.
 * Copies from localStorage when sessionStorage is empty (e.g. new tab),
 * and backfills localStorage when only sessionStorage has data (pre-migration).
 * Returns true when userInfo is available.
 */
export function ensureSessionAuthFromLocal(): boolean {
  let userInfo = sessionStorage.getItem(USER_INFO_KEY);
  const localUserInfo = localStorage.getItem(USER_INFO_KEY);

  if (!userInfo && localUserInfo) {
    sessionStorage.setItem(USER_INFO_KEY, localUserInfo);
    userInfo = localUserInfo;
  } else if (userInfo && !localUserInfo) {
    localStorage.setItem(USER_INFO_KEY, userInfo);
  }

  const sessionPermissions = sessionStorage.getItem(USER_PERMISSIONS_KEY);
  const localPermissions = localStorage.getItem(USER_PERMISSIONS_KEY);

  if (!sessionPermissions && localPermissions) {
    sessionStorage.setItem(USER_PERMISSIONS_KEY, localPermissions);
  } else if (sessionPermissions && !localPermissions) {
    localStorage.setItem(USER_PERMISSIONS_KEY, sessionPermissions);
  }

  return !!userInfo;
}

export function clearAuthStorage() {
  sessionStorage.removeItem(USER_INFO_KEY);
  sessionStorage.removeItem(USER_PERMISSIONS_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(USER_PERMISSIONS_KEY);
  localStorage.removeItem(IS_AUTHENTICATED_KEY);
  // Perk Point "log in as manager" session (see perkpoint/programs/program-scope.ts)
  localStorage.removeItem("perkpoint.managerSession");
}
